/** Takvim yardımcıları — MBS Panel */

export const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
export const WEEKDAYS_FULL = [
    "Pazartesi",
    "Salı",
    "Çarşamba",
    "Perşembe",
    "Cuma",
    "Cumartesi",
    "Pazar",
];

export const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 07–21

export const SOURCE_STYLES = {
    randevu: {
        label: "Randevu",
        dot: "bg-sky-400",
        className: "bg-sky-500/20 text-sky-100 border-sky-500/35",
        ring: "ring-sky-500/40",
    },
    hatirlatma: {
        label: "Hatırlatma",
        dot: "bg-amber-400",
        className: "bg-amber-500/20 text-amber-100 border-amber-500/35",
        ring: "ring-amber-500/40",
    },
    takvim: {
        label: "Sistem",
        dot: "bg-violet-400",
        className: "bg-violet-500/20 text-violet-100 border-violet-500/35",
        ring: "ring-violet-500/40",
    },
};

export const ETKINLIK_TUR_STYLES = {
    DAVA: { label: "Dava", className: "bg-rose-500/20 text-rose-200 border-rose-500/30" },
    ODEME: { label: "Ödeme", className: "bg-emerald-500/20 text-emerald-200 border-emerald-500/30" },
    ICRA: { label: "İcra", className: "bg-orange-500/20 text-orange-200 border-orange-500/30" },
    TAHKIM: { label: "Tahkim", className: "bg-indigo-500/20 text-indigo-200 border-indigo-500/30" },
    DIGER: { label: "Diğer", className: "bg-slate-500/20 text-slate-200 border-slate-500/30" },
};

export const ONEM_STYLES = {
    yuksek: "border-l-rose-500",
    orta: "border-l-amber-500",
    dusuk: "border-l-slate-500",
};

export function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function startOfWeek(d) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}

export function endOfWeek(d) {
    const s = startOfWeek(d);
    const e = new Date(s);
    e.setDate(e.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
}

export function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

export function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function toYmd(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export function parseApiDate(raw) {
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function formatTime(d) {
    if (!d) return "—";
    return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

export function formatDateLong(d) {
    if (!d) return "—";
    return d.toLocaleDateString("tr-TR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

export function formatDateShort(d) {
    if (!d) return "—";
    return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
}

export function isSameDay(a, b) {
    if (!a || !b) return false;
    return toYmd(a) === toYmd(b);
}

export function isToday(d) {
    return isSameDay(d, new Date());
}

/** API event → UI event */
export function normalizeApiEvent(ev) {
    const start = parseApiDate(ev.start);
    const end = parseApiDate(ev.end) || start;
    if (!start) return null;
    return {
        ...ev,
        start,
        end,
        dayKeys: expandDayKeys(start, end),
    };
}

export function expandDayKeys(start, end) {
    const keys = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= last) {
        keys.push(toYmd(cur));
        cur = addDays(cur, 1);
    }
    return keys;
}

export function eventStyle(ev) {
    if (ev.source === "takvim" && ev.meta?.etkinlik_turu) {
        return (
            ETKINLIK_TUR_STYLES[ev.meta.etkinlik_turu] ||
            SOURCE_STYLES.takvim
        );
    }
    return SOURCE_STYLES[ev.source] || SOURCE_STYLES.takvim;
}

export function eventBadgeLabel(ev) {
    if (ev.source === "takvim") {
        return ev.meta?.etkinlik_turu_display || SOURCE_STYLES.takvim.label;
    }
    if (ev.source === "randevu") {
        return ev.meta?.durum_display || SOURCE_STYLES.randevu.label;
    }
    return SOURCE_STYLES[ev.source]?.label || "Etkinlik";
}

export function rangeForView(view, cursor) {
    if (view === "week") {
        return { from: toYmd(startOfWeek(cursor)), to: toYmd(endOfWeek(cursor)) };
    }
    if (view === "day") {
        return { from: toYmd(cursor), to: toYmd(cursor) };
    }
    if (view === "agenda") {
        const s = new Date();
        const e = addDays(s, 30);
        return { from: toYmd(s), to: toYmd(e) };
    }
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    return { from: toYmd(start), to: toYmd(end) };
}

export function filterEvents(events, { sources, search, etkinlikTuru, durum }) {
    let rows = events;
    if (sources && sources.size > 0) {
        rows = rows.filter((e) => sources.has(e.source));
    }
    if (etkinlikTuru) {
        rows = rows.filter(
            (e) => e.source !== "takvim" || e.meta?.etkinlik_turu === etkinlikTuru,
        );
    }
    if (durum) {
        rows = rows.filter(
            (e) => e.source !== "randevu" || e.meta?.durum === durum,
        );
    }
    if (search?.trim()) {
        const q = search.trim().toLowerCase();
        rows = rows.filter(
            (e) =>
                (e.title || "").toLowerCase().includes(q) ||
                (e.description || "").toLowerCase().includes(q) ||
                (e.meta?.muvekkil_adi || "").toLowerCase().includes(q) ||
                (e.meta?.dosya_no || "").toLowerCase().includes(q),
        );
    }
    return rows;
}

export function eventsOnDay(events, dayKey) {
    return events.filter((ev) => ev.dayKeys?.includes(dayKey));
}

export function eventsInHour(events, dayKey, hour) {
    return eventsOnDay(events, dayKey).filter((ev) => {
        if (ev.all_day) return hour === 7;
        return ev.start.getHours() === hour;
    });
}
